<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
          <h1 class="text-h4 mb-0">{{ $t('budgetLines.title') }}</h1>
          <v-btn color="primary" @click="openAddDialog()">{{ $t('budgetLines.add') }}</v-btn>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>{{ $t('budgetLines.list') }}</v-card-title>
          <v-card-text>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>{{ $t('budgetLines.id') }}</th>
                  <th>{{ $t('budgetLines.name') }}</th>
                  <th>{{ $t('budgetLines.parent') }}</th>
                  <th>{{ $t('budgetLines.order') }}</th>
                  <th>{{ $t('budgetLines.active') }}</th>
                  <th>{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in flatRows" :key="row.id">
                  <td>{{ row.id }}</td>
                  <td :style="{ paddingLeft: (row.depth * 24 + 16) + 'px' }">
                    <v-icon size="small" class="mr-1">{{ row.is_folder ? 'mdi-folder' : 'mdi-file-document-outline' }}</v-icon>
                    <span :class="{ 'font-weight-bold': row.is_folder }">{{ row.name }}</span>
                  </td>
                  <td>{{ row.parentName }}</td>
                  <td>{{ row.order }}</td>
                  <td>
                    <v-chip :color="row.active ? 'success' : 'grey'" size="small" variant="flat">
                      {{ row.active ? $t('budgetLines.yes') : $t('budgetLines.no') }}
                    </v-chip>
                  </td>
                  <td>
                    <v-btn v-if="row.is_folder" variant="plain" size="small" @click="openAddDialog(row.id)" icon="mdi-plus" :title="$t('budgetLines.addChild')"></v-btn>
                    <v-btn variant="plain" size="small" @click="editBudgetLine(row)" icon="mdi-pencil"></v-btn>
                    <v-btn variant="plain" size="small" @click="confirmDelete(row)" icon="mdi-delete" color="error"></v-btn>
                  </td>
                </tr>
                <tr v-if="budgetLines.length === 0">
                  <td colspan="6" class="text-center text-grey">{{ $t('budgetLines.noBudgetLinesFound') }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="showAddDialog" max-width="500" @update:model-value="(val: boolean) => { if (!val) resetForm(); }">
      <v-card>
        <v-card-title>{{ form.id ? $t('budgetLines.edit') : $t('budgetLines.add') }}</v-card-title>
        <v-card-text>
          <v-form>
            <v-text-field
              v-model="form.name"
              :label="$t('budgetLines.name')"
              :placeholder="$t('budgetLines.namePlaceholder')"
              required
              :rules="[v => !!v || $t('budgetLines.nameRequired')]"
            />
            <v-select
              v-model="form.parent_id"
              :items="parentOptions"
              item-title="name"
              item-value="id"
              :label="$t('budgetLines.parent')"
              clearable
            />
            <v-text-field
              v-model.number="form.order"
              :label="$t('budgetLines.order')"
              type="number"
            />
            <v-switch
              v-model="form.is_folder"
              :label="$t('budgetLines.isFolder')"
              color="primary"
            />
            <v-switch
              v-model="form.active"
              :label="$t('budgetLines.active')"
              color="success"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showAddDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveBudgetLine">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>{{ $t('common.confirm') }}</v-card-title>
        <v-card-text>{{ $t('budgetLines.areYouSureDelete') }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showDeleteDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="error" @click="deleteBudgetLine">{{ $t('common.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog v-model="showErrorDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6 text-error">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          {{ $t('errors.unknownError') }}
        </v-card-title>
        <v-card-text>
          <p class="text-body-1">{{ errorMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showErrorDialog = false">{{ $t('errors.ok') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiGet, apiPost, apiPut, apiDelete, getErrorMessage } from '../utils/api'

const { t } = useI18n()

interface BudgetLine {
  id: number
  name: string
  parent_id: number | null
  order: number
  is_folder: boolean
  active: boolean
}

interface FlatRow extends BudgetLine {
  depth: number
  parentName: string
}

const budgetLines = ref<BudgetLine[]>([])
const showAddDialog = ref(false)
const showDeleteDialog = ref(false)
const showErrorDialog = ref(false)
const errorMessage = ref('')
const deleteTarget = ref<BudgetLine | null>(null)

const form = ref({
  id: null as number | null,
  name: '',
  parent_id: null as number | null,
  order: 0,
  is_folder: false,
  active: true
})

// Build a name lookup for parent display
const nameById = computed(() => {
  const map: Record<number, string> = {}
  budgetLines.value.forEach(bl => { map[bl.id] = bl.name })
  return map
})

// Recursively flatten the tree into rows with depth
const flatRows = computed<FlatRow[]>(() => {
  const rows: FlatRow[] = []
  const sort = (a: BudgetLine, b: BudgetLine) => a.order - b.order || a.name.localeCompare(b.name)

  const walk = (parentId: number | null, depth: number) => {
    const children = budgetLines.value
      .filter(bl => bl.parent_id === parentId)
      .sort(sort)

    for (const child of children) {
      rows.push({
        ...child,
        depth,
        parentName: child.parent_id != null ? (nameById.value[child.parent_id] ?? '—') : '—'
      })
      walk(child.id, depth + 1)
    }
  }

  walk(null, 0)
  return rows
})

// Only folders can be parents (show full path for clarity)
const parentOptions = computed(() => {
  const buildLabel = (id: number): string => {
    const bl = budgetLines.value.find(b => b.id === id)
    if (!bl) return ''
    if (bl.parent_id == null) return bl.name
    return buildLabel(bl.parent_id) + ' / ' + bl.name
  }

  return budgetLines.value
    .filter(bl => bl.is_folder && bl.id !== form.value.id)
    .map(bl => ({ id: bl.id, name: buildLabel(bl.id) }))
})

const loadBudgetLines = async () => {
  try {
    const response = await apiGet('/api/budget-lines')
    if (response.ok) {
      budgetLines.value = await response.json()
    }
  } catch (error) {
    console.error('Load budget lines error:', error)
  }
}

const openAddDialog = (parentId: number | null = null) => {
  resetForm()
  form.value.parent_id = parentId
  showAddDialog.value = true
}

const editBudgetLine = (bl: BudgetLine) => {
  form.value = {
    id: bl.id,
    name: bl.name,
    parent_id: bl.parent_id,
    order: bl.order,
    is_folder: bl.is_folder,
    active: bl.active
  }
  showAddDialog.value = true
}

const resetForm = () => {
  form.value = {
    id: null,
    name: '',
    parent_id: null,
    order: 0,
    is_folder: false,
    active: true
  }
}

const showError = (message: string) => {
  errorMessage.value = message
  showErrorDialog.value = true
}

const saveBudgetLine = async () => {
  if (!form.value.name) {
    showError(t('budgetLines.fillName'))
    return
  }

  try {
    let response
    const payload = {
      budget_line: {
        name: form.value.name,
        parent_id: form.value.parent_id,
        order: form.value.order,
        is_folder: form.value.is_folder,
        active: form.value.active
      }
    }

    if (form.value.id) {
      response = await apiPut(`/api/budget-lines/${form.value.id}`, payload)
    } else {
      response = await apiPost('/api/budget-lines', payload)
    }

    if (response.ok) {
      await loadBudgetLines()
      showAddDialog.value = false
      resetForm()
    } else {
      const errorText = await getErrorMessage(response, t('errors.unknownError'))
      showError(t('budgetLines.failedToSave', { errors: errorText }))
    }
  } catch (error) {
    console.error('Save budget line error:', error)
    const errorText = error instanceof Error ? error.message : String(error)
    showError(t('budgetLines.errorSaving', { errors: errorText }))
  }
}

const confirmDelete = (bl: BudgetLine) => {
  deleteTarget.value = bl
  showDeleteDialog.value = true
}

const deleteBudgetLine = async () => {
  if (!deleteTarget.value) return
  try {
    const response = await apiDelete(`/api/budget-lines/${deleteTarget.value.id}`)
    if (response.ok) {
      await loadBudgetLines()
      showDeleteDialog.value = false
      deleteTarget.value = null
    } else {
      showError(t('budgetLines.failedToDelete'))
    }
  } catch (error) {
    console.error('Delete budget line error:', error)
    showError(t('budgetLines.errorDeleting'))
  }
}

onMounted(() => {
  loadBudgetLines()
})
</script>
